import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp } from 'lucide-react';
import VoiceInputButton from './VoiceInputButton';
import API_BASE_URL from '../config';
import './SuggestedPlan.css';

const BACKEND_URL = API_BASE_URL;

function SuggestedPlan({ taskText, taskDate, open, onClose, liftAboveComposer }) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  // Initial suggestion fetch when opened or when task changes
  const fetchSuggestion = async (isInitial = true, userQuery = '') => {
    setLoading(true);
    const token = localStorage.getItem('accessToken');

    // Add user message if this is a chat query
    if (!isInitial && userQuery) {
      setMessages(prev => [...prev, { type: 'user', text: userQuery }]);
    }

    if (!token) {
      setMessages(prev => [...prev, { type: 'ai', text: "Please log in to chat with me!" }]);
      setLoading(false);
      return;
    }

    try {
      const payload = isInitial
        ? { task: taskText, task_date: taskDate }
        : { task: userQuery, task_date: new Date().toISOString() }; // Reuse endpoint for generic chat for now

      const response = await fetch(`${BACKEND_URL}/api/suggest/get_suggestion/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      const aiResponse = data.gpt_suggestion || "I'm here to help with your tasks!";
      const priceTip = data.price_tip;
      const prices = data.prices || {};

      setMessages(prev => {
        const newMsgs = [...prev, { type: 'ai', text: aiResponse }];

        if (priceTip) {
          newMsgs.push({ type: 'ai', text: `💰 Tip: ${priceTip}` });
        }

        if (Object.keys(prices).length > 0) {
          let priceMsg = "Comparison:\n";
          Object.entries(prices).forEach(([store, items]) => {
            if (Array.isArray(items)) {
              items.forEach(item => {
                priceMsg += `• ${store.charAt(0).toUpperCase() + store.slice(1)}: ${item.item} (${item.price})\n`;
              });
            }
          });
          newMsgs.push({ type: 'ai', text: priceMsg });
        }

        return newMsgs;
      });

    } catch (err) {
      console.error("❌ Error fetching suggestion:", err);
      setMessages(prev => [...prev, { type: 'ai', text: "I'm having trouble connecting right now." }]);
    } finally {
      setLoading(false);
    }
  };

  // Seed the greeting (and an initial task suggestion, if any) the first time
  // the assistant is opened from the Header.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ type: 'ai', text: "Hi! I'm Tudu. I can help you with smart suggestions for your cart, modify your planned tasks, or assist you with anything you need!" }]);
      if (taskText) {
        fetchSuggestion(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    fetchSuggestion(false, inputText);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className={`tudu-assistant-container${liftAboveComposer ? ' above-composer' : ''}`}>
      {/* Chat Window — opened from the Header "Ask" button (controlled) */}
      {open && (
        <div className="assistant-chat-window">
          <div className="assistant-header">
            <div className="assistant-header-avatar">
              <img src="/images/tudu_logo.png" alt="Tudu AI" />
            </div>
            <span className="assistant-name">Tudu Assistant</span>
            <button className="close-btn" onClick={onClose}>✖</button>
          </div>

          <div className="assistant-content">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.type}`}>
                {msg.text.split('\n').map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            ))}

            {loading && (
              <div className="assistant-loading">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="assistant-input-area">
            <input
              className="assistant-input"
              placeholder="Ask me anything..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <VoiceInputButton onResult={(t) => setInputText((prev) => (prev ? `${prev} ` : '') + t)} />
            <button className="send-btn" onClick={handleSend} aria-label="Send">
              <ArrowUp size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuggestedPlan;
